import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { change, Field, formValueSelector } from 'redux-form';
import { Button, FormGroup } from 'reactstrap';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import FormItem from '../../../common/forms/FormItem';
import {
  CONTRACT_TYPE,
  CREDIT_NOTE,
  TRANSACTION_TYPE_BUSINESS_CREDIT,
  TRANSACTION_TYPE_CREDIT_NOTE,
  TRANSACTION_TYPE_OTHER,
  TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL,
} from '../../../../config/constants';
import { isRequired } from '../../../../utils/validators';
import TextInput from '../../../common/forms/input/TextInput';
import Select from '../../../common/forms/select/Select';
import {
  DEFAULT_RIDE_BOOKING_PAYMENT_METHOD_OPTION,
  DEFAULT_VOUCHER_TYPE_OPTION,
} from '../../../../config/defaults';
import { clearCreditNotes, getCreditNotes } from '../../../../actions';
import VoucherTypeInputGroup from '../../../common/forms/VoucherTypeInputGroup';
import MultiPaymentMethodInputGroup from '../../../common/forms/MultiPaymentMethodInputGroup';
import { getRowTransactionCode } from '../../../../utils/units/paymentSplit';

const FORM_NAME = 'rideBookingPassengerForm';

const CUSTOMER_SOURCE = 'CUSTOMER';

const selector = formValueSelector(FORM_NAME);

export const PaymentForm = ({
  totalPrice,
  paymentMethods,
  defaultPaymentMethod,
  dispatchChange,
  dispatchGetCreditNotes,
  dispatchClearCreditNotes,
  discountCodeValue,
  appliedDiscountCodeValue,
  onApplyDiscountCode,
  onClearDiscountCode,
  quoting,
  passengersValue,
  customerValue,
  transactionListValues,
  seatReservations,
  processingPayment,
  totalPosLoading,
}) => {
  // credit notes only for a single passenger paid by a registered customer:
  // a PERSON has no notes of its own and the backend doesn't check ownership
  const creditNoteCustomerId =
    customerValue &&
    customerValue.source === CUSTOMER_SOURCE &&
    customerValue.id &&
    seatReservations.length === 1
      ? customerValue.id
      : null;

  const hasPinpadCharge = transactionListValues.some(
    (row) => row && row.pinpadPaid,
  );

  useLayoutEffect(() => {
    dispatchChange(FORM_NAME, 'voucherType', DEFAULT_VOUCHER_TYPE_OPTION);

    return () => {
      dispatchClearCreditNotes();
    };
  }, []);

  useEffect(() => {
    dispatchClearCreditNotes();

    // notes picked for the previous payer must not be sent for the new one
    transactionListValues.forEach((row, index) => {
      if (getRowTransactionCode(row) === TRANSACTION_TYPE_CREDIT_NOTE) {
        dispatchChange(FORM_NAME, `transactionList[${index}].creditNotes`, null);
        dispatchChange(FORM_NAME, `transactionList[${index}].amount`, null);
      }
    });

    if (creditNoteCustomerId) {
      dispatchGetCreditNotes({
        query: [
          `customerId:${creditNoteCustomerId}`,
          'isValid:true',
          'previousTicketId:!Ø',
        ],
      });
    }
  }, [creditNoteCustomerId]);

  useEffect(() => {
    // keep the single payment row in sync when a discount changes the total
    const [firstRow] = transactionListValues;
    if (
      transactionListValues.length === 1 &&
      firstRow &&
      !firstRow.pinpadPaid &&
      getRowTransactionCode(firstRow) !== TRANSACTION_TYPE_CREDIT_NOTE
    ) {
      dispatchChange(FORM_NAME, 'transactionList[0].amount', totalPrice);
    }
  }, [totalPrice]);

  const passengerList = useMemo(() => {
    if (!passengersValue || passengersValue.length === 0) {
      return [];
    }

    return passengersValue
      .filter((item) => item.passenger)
      .map((item) => ({
        id: item.passenger.id,
        value: item.passenger.value,
        label: item.passenger.label,
        firstName: item.passenger.firstName,
        lastName: item.passenger.lastName,
        documentNumber: item.passenger.idDocumentNumber,
        identificationType: item.passenger.identificationTypeName,
        dob: item.passenger.dob,
        countryCode: item.passenger.countryCode,
        countryName: item.passenger.countryName,
        source: item.passenger.source,
      }));
  }, [passengersValue]);

  useEffect(() => {
    // the payer must be one of the current passengers
    if (passengerList.length === 1) {
      const [onlyPassenger] = passengerList;
      if (!customerValue || customerValue.value !== onlyPassenger.value) {
        dispatchChange(FORM_NAME, 'customer', onlyPassenger);
      }
      return;
    }

    if (
      !customerValue ||
      passengerList.some((item) => item.value === customerValue.value)
    ) {
      return;
    }

    // same person under a new value, e.g. a PERSON turned into a Customer
    const payerDocument =
      customerValue.documentNumber || customerValue.idDocumentNumber;
    const samePerson = payerDocument
      ? passengerList.find((item) => item.documentNumber === payerDocument)
      : undefined;

    dispatchChange(FORM_NAME, 'customer', samePerson || null);
  }, [passengerList]);

  // totals come from /ride-booking/quote, priced by the parent form
  const handleApplyDiscountCode = () => {
    if (hasPinpadCharge || totalPosLoading) {
      toastr.warning(
        'Advertencia',
        'Cancele el cobro en el pinpad antes de aplicar un código de descuento.',
      );
      return;
    }

    if (!discountCodeValue || discountCodeValue.trim() === '') {
      toastr.warning('Advertencia', 'Debe ingresar un código de descuento.');
      return;
    }

    onApplyDiscountCode(discountCodeValue.trim());
  };

  const isDiscountApplied = Boolean(appliedDiscountCodeValue);

  const transactionCodesNotIncluded = [
    TRANSACTION_TYPE_OTHER,
    TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL,
  ];

  if (!creditNoteCustomerId) {
    transactionCodesNotIncluded.push(TRANSACTION_TYPE_CREDIT_NOTE);
  }

  return (
    <div>
      <br />
      <h2>Pago</h2>
      <FormGroup row>
        <FormItem label="Cliente" required>
          <Field
            name="customer"
            component={Select}
            options={passengerList}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <VoucherTypeInputGroup
        voucherTypeFieldName="voucherType"
        businessFieldName="business"
        businessTaxIdFieldName="businessTaxId"
        businessNameFieldName="businessName"
        addressSummaryFieldName="addressSummary"
        change={change}
      />
      <hr />
      <FormGroup row>
        <FormItem label="Código de descuento">
          <Field
            name="discountCode"
            component={TextInput}
            type="text"
            placeholder="Código de descuento"
            disabled={isDiscountApplied}
          />
          {hasPinpadCharge && !isDiscountApplied && (
            <small className="text-muted">
              Para aplicar un código cancele primero el cobro del pinpad
            </small>
          )}
          {isDiscountApplied ? (
            <Button
              className="pull-right mt-1"
              type="button"
              color="danger"
              outline
              onClick={onClearDiscountCode}
              disabled={processingPayment || totalPosLoading || quoting}
            >
              Limpiar código de descuento
            </Button>
          ) : (
            <Button
              className="pull-right mt-1"
              type="button"
              onClick={handleApplyDiscountCode}
              disabled={
                processingPayment || hasPinpadCharge || totalPosLoading || quoting
              }
            >
              Aplicar Descuento
            </Button>
          )}
        </FormItem>
      </FormGroup>
      <MultiPaymentMethodInputGroup
        formName={FORM_NAME}
        totalPrice={totalPrice}
        recomendedPaymentMethod={paymentMethods}
        defaultPaymentMethod={defaultPaymentMethod}
        transactionCodesNotIncluded={transactionCodesNotIncluded}
      />
    </div>
  );
};

PaymentForm.propTypes = {
  totalPrice: PropTypes.number.isRequired,
  paymentMethods: Select.propTypes.options.isRequired,
  defaultPaymentMethod: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  }),
  dispatchChange: PropTypes.func.isRequired,
  dispatchGetCreditNotes: PropTypes.func.isRequired,
  dispatchClearCreditNotes: PropTypes.func.isRequired,
  discountCodeValue: PropTypes.string,
  appliedDiscountCodeValue: PropTypes.string,
  onApplyDiscountCode: PropTypes.func.isRequired,
  onClearDiscountCode: PropTypes.func.isRequired,
  quoting: PropTypes.bool,
  passengersValue: PropTypes.arrayOf(PropTypes.shape({})),
  customerValue: PropTypes.shape({
    id: PropTypes.number,
    value: PropTypes.string,
    source: PropTypes.string,
    documentNumber: PropTypes.string,
    idDocumentNumber: PropTypes.string,
  }),
  transactionListValues: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  seatReservations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  processingPayment: PropTypes.bool,
  totalPosLoading: PropTypes.bool.isRequired,
};

PaymentForm.defaultProps = {
  defaultPaymentMethod: undefined,
  discountCodeValue: null,
  appliedDiscountCodeValue: null,
  quoting: false,
  passengersValue: null,
  customerValue: null,
  processingPayment: false,
};

const mapStateToProps = (state, { totalPrice }) => {
  const paymentMethods = state.AccountingUnit.PaymentMethod.getIn([
    'all',
    'content',
    'content',
  ]).map((paymentMethod) => ({
    value: paymentMethod.id,
    label: paymentMethod.name,
    transactionCode: paymentMethod.transactionCode,
  }));

  let newPaymentMethods = paymentMethods;

  let defaultPaymentMethod = DEFAULT_RIDE_BOOKING_PAYMENT_METHOD_OPTION;

  const { salesSessionUserId } = state.authentication.get('user');

  const activeSaleSession = state.SalesUnit.SalesSession.getIn([
    'active',
    'content',
  ]);

  const agency = activeSaleSession.get('agency');
  const contractType = agency ? agency.contractType : null;

  if (salesSessionUserId) {
    defaultPaymentMethod = undefined;
  } else if (contractType && contractType === CONTRACT_TYPE.CORPORATE.value) {
    defaultPaymentMethod = undefined;

    newPaymentMethods = paymentMethods.filter(
      (method) =>
        method.transactionCode === CREDIT_NOTE ||
        method.transactionCode === TRANSACTION_TYPE_BUSINESS_CREDIT,
    );
  }

  // the form total is the quoted one, the stored seat total is only a fallback
  const formTotal = parseFloat(selector(state, 'total'));

  return {
    totalPrice: Number.isNaN(formTotal) ? totalPrice : formTotal,
    paymentMethods: newPaymentMethods,
    defaultPaymentMethod,
    discountCodeValue: selector(state, 'discountCode'),
    appliedDiscountCodeValue: selector(state, 'appliedDiscountCode'),
    passengersValue: selector(state, 'passengers'),
    customerValue: selector(state, 'customer'),
    transactionListValues: selector(state, 'transactionList') || [],
    seatReservations: state.BookingUnit.Booking.getIn([
      'seats',
      'seatReservations',
    ]).toJS(),
    processingPayment: state.BookingUnit.Booking.getIn([
      'payment',
      'postingPayment',
    ]),
    totalPosLoading: !state.TotalPosUnit.TotalPos.getIn([
      'current',
      'activity',
    ]).isEmpty(),
  };
};

const mapDispatchToProps = {
  dispatchGetCreditNotes: getCreditNotes,
  dispatchClearCreditNotes: clearCreditNotes,
  dispatchChange: change,
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentForm);
